const img = (name: string): string => `/images/${name}`

export const hero = {
    wordmark: 'Selora',
    statement: ['I break things', 'to see WHAT', 'THEY ARE MADE OF'] as const,
    image: img('hero-ascent.png'),
    explore: 'explore',
    phases: [
        { index: '001', label: 'PHASE/', value: 'BREAK', fill: 0.428 },
        { index: '002', label: 'PHASE/', value: 'BUILD', fill: 0.338 },
        { index: '003', label: 'PHASE/', value: 'BEND', fill: 0.562 },
        { index: '004', label: 'PHASE/', value: 'RELEASE', fill: 0.24 }
    ] as const,
    skills: [
        'VISUAL EXPERIMENTS',
        'FORM & FUNCTION',
        'SOUND & MOTION',
        'WRITTEN FRAGMENTS',
        'THINGS I CAN’T EXPLAIN'
    ] as const
}

export const intro = {
    image: img('breath-study.png'),
    caption: 'Release without restraint',
    captionLabel: 'Photography',
    title: ['ART IS A CONTROLLED', 'INTERRUPTION'] as const,
    subtitle:
        'A PRACTICE OF CATCHING THE MOMENT BEFORE IT DISAPPEARS. I WORK ACROSS IMAGE, OBJECT, MOTION, AND SOUND TO TRACE THE SHAPE OF WHAT DOESN’T SIT STILL.',
    note: 'It isn’t a portfolio. It’s the place where the work stays honest.',
    footnote:
        'AN ongoing record of what I make when thought moves faster than structure.'
}

export const gallery = [
    {
        src: img('departure-study.png'),
        caption: 'A pause between departures',
        label: 'Motion Study',
        place: 'New York (2025)'
    },
    {
        src: img('veil-study.png'),
        caption: 'Concealment as a form of power.',
        label: 'Fashion',
        place: 'Paris (2023)'
    },
    {
        src: img('bloom-study.png'),
        caption: 'Quiet strength in full bloom',
        label: 'Photography',
        place: 'Singapore (2014)'
    },
    {
        src: img('dancer-study.png'),
        caption: 'Discipline, held in motion',
        label: 'Movement',
        place: 'Osaka (2019)'
    },
    {
        src: img('passage-study.png'),
        caption: 'Caught between who you were and who remains',
        label: 'Light Experiment',
        place: 'Brighton (2018)'
    },
    {
        src: img('sculpture-study.png'),
        caption: 'Silence shaped into form',
        label: 'Exhibition',
        place: 'Sydney (2021)'
    }
] as const

export const concept = {
    image: img('uBxP9U1PLV67RuuwTdMdbEkQA.jpg'),
    imageTitle: 'INDX',
    kicker: '// CONCEPTUAL',
    revision: 'REVISION — NEUE 7.6',
    title: ['NOTHING', 'STAYS', 'UNTOUCHED'] as const,
    description:
        'Pages become places worth lingering in, and issues become experiences people anticipate, keep, and share.',
    strapline: ['PERSPECTIVE', 'NOT THE TRUTH'] as const,
    catalog: [
        {
            code: 'CAT — 1.07',
            body: 'I work between order and interruption. Where clean lines argue with impulse. Where rhythm breaks before it resolves.'
        },
        {
            code: 'CAT — 1.08',
            body: 'Selora is the state I build in— a place for unfinished thoughts, sharpened ideas, and the things that refuse silence.'
        }
    ] as const
}

export const process = {
    label: 'EXPLORATION PHASE',
    title: ['Some pieces settle.', 'Some don’t.'] as const,
    subtitle: 'Both reveal something the finished version can’t.',
    source: 'SOURCE',
    sourceNote: '— FIELD NOTES',
    background: img('2WRE967h2vJrkIS9mZJ3CRlpMI.jpeg'),
    modules: [
        {
            code: 'MODULE — A.1',
            body: 'A section of studies in raw structure. Textures tested under pressure. Forms pushed until they reveal intention.'
        },
        {
            code: 'MODULE — A.2',
            body: 'Experiments with physical materials and controlled distortion. Where touch, weight, and failure shape the outcome.'
        },
        {
            code: 'MODULE — A.3',
            body: 'Objects examined through repetition. Small shifts creating new patterns. A record of how matter responds to motion.'
        },
        {
            code: 'MODULE — A.4',
            body: 'Fragments from ongoing investigations. Part prototypes, part unresolved ideas. Work that stays honest by not pretending to be finished.'
        }
    ] as const
}

export const featured = {
    marquee: 'MODERN RITUALS',
    study: 'STUDY — 04.13',
    kicker: 'SELECTED WORK',
    headline: ['LINES', 'BECOME', 'SIGNALS.'] as const,
    headlineTwo: ['SURFACES', 'BECOME', 'STORIES.'] as const,
    artistDescription:
        'STRUCTURE ARGUES WITH IMPULSE UNTIL BOTH LEARN TO STAND STILL.',
    editorial: 'GRIDS SET THE PACE. MARGINS HOLD THE QUIET.',
    connected: '[CONFESS]',
    thumbnails: [
        {
            src: img('mRBiQFDuE4B7QVSEklk21DWcM.jpeg'),
            caption: 'ARTIFACT—I'
        },
        {
            src: img('gLqCslm3A6i3AkKnPkWHelWtkNo.jpg'),
            caption: 'ARTIFACT—II'
        },
        {
            src: img('3dhrjEKd33WmxaQ1Mtixa0iI3M4.jpeg'),
            caption: 'ARTIFACT—III'
        }
    ] as const,
    wideTop: img('passage-study.png'),
    wideBottom: img('9Hbjl9ztDSwlBJuq6SREqQR8Dg.jpeg')
}

export const workTypes = {
    headline: [
        'the archive of everything',
        'I can’t keep in one place.'
    ] as const,
    storage: 'SELORA STORAGE',
    years: '2015-2026',
    archive: 'ANALOG ARCHIVES',
    features: [
        {
            mod: 'MOD — I/AK',
            title: 'VISUAL EXPERIMENTS',
            number: '001',
            body: 'Studies in image, light, and distortion. Tests that don’t follow rules. Pieces built from instinct, error, and the urge to see what happens next.'
        },
        {
            mod: 'MOD — II/AK',
            title: 'FORM & FUNCTION',
            number: '002',
            body: 'Objects, systems, and shapes shaped with intention — then pushed until they reveal their limits. A dialogue between what looks right and what works.'
        },
        {
            mod: 'MOD — III/AK',
            title: 'SOUND & MOTION',
            number: '003',
            body: 'Moving images, rhythm studies, and audiovisual fragments. Work driven by pulse, tension, and the quiet between frames.'
        },
        {
            mod: 'MOD — IV/AK',
            title: 'WRITTEN FRAGMENTS',
            number: '004',
            body: 'Poems, lyrics, and unfinished lines. Thoughts caught mid-breath. Words that behave more like images than sentences.'
        },
        {
            mod: 'MOD — V/AK',
            title: 'THINGS I CAN’T EXPLAIN',
            number: '005',
            body: 'Ideas that arrived uninvited and refused to leave. The work that sits closest to who I am and who I’m still becoming.'
        }
    ] as const
}

export const statement = {
    imageTop: img('signal-portrait.png'),
    imageBottom: img('rgK3PpbanQ4CVwmKdKQJ7HHUFc.jpeg'),
    messageOne: 'PATTERNS EMERGE.',
    messageTwo: 'FRICTION CREATES MEANING.',
    lines: 'Signals form. Surfaces respond.',
    features: ['ILLUSION', 'LATENCY', 'PERSPECTIVE', 'CONTROL'] as const,
    headline: ['EMBRACING', 'THE UNKNOWN'] as const,
    subtitle:
        'I follow ideas into places that don’t have names yet. Some reveal structure. Some collapse into noise.',
    footnote:
        'Work shaped by movement, memory, and interruption. Studies in light, depth, and distortion. Each piece begins as a question and ends wherever it needs to.',
    image: img('motion-traced.png'),
    imageCaption: 'Motion traced, not defined',
    imageLabel: 'THE UNKNOWN'
}

export const philosophy = {
    left: 'WHAT HOLDS UP IS WHAT MATTERS.',
    leftNote: 'Observation over explanation. Process over certainty.',
    right:
        'I follow ideas into places that shift as I step into them. Paths appear, vanish, reappear somewhere else. Some lead to clarity. Some lead to noise. Both keep the rabbit moving.',
    rabbit: img('butterfly-mark.png'),
    circularText: 'CHASING THE WHITE RABBIT * CHASING THE WHITE RABBIT * ',
    headline: 'CHASING THE WHITE RABBIT*',
    signatureText:
        'I chase the things that change direction without warning. A line bends. A thought splits. A shape becomes something it wasn’t meant to be. I stay with it until it reveals a reason to follow. The rabbit is never still.',
    signature: img('fU3WmM3L1vdP5RLFKaBh2WUqVs.svg'),
    tag: 'SELORA',
    name: ['Jayson', 'CASTLE'] as const,
    cards: [
        {
            title: 'VISUAL',
            body: 'Images pulled from movement, memory, and interruption. Studies in light, depth, and distortion. Work built from the urge to see what happens next.'
        },
        {
            title: 'FORM',
            body: 'Objects, systems, and structures under tension. Where function bends into expression. Tests built to reveal how materials behave when pushed.'
        },
        {
            title: 'MOTION',
            body: 'Frames driven by rhythm and atmosphere. Loops, pulses, and shifting perspectives. Pieces meant to be felt before they’re understood.'
        }
    ] as const
}

export const showcase = {
    quote:
        '“Whether on paper or pixels, the goal is constant — design that disappears as the story appears, letting the work speak without shouting for attention”',
    quoteAuthor: '— JC',
    image: img('WgSXpdQNIRyyqbHxMdMqlYBg4k.jpeg'),
    imageCaption: 'Studio CAM66, London',
    imageLabel: 'Daniel & Jayson',
    title: 'STUDIO CHAT WITH DANIEL MOORE',
    subtitle:
        'Jayson talks about breaking form, chasing rhythm, and shaping thought into images.',
    recorded:
        'Recorded AT CAM66 Studios London in 24 November 2025',
    duration: '15 minutes, 13 seconds',
    format: 'Digital Media'
}

export const exhibition = {
    image: img('VP537IhNaTLW7QOkMnGSrpCPh0.jpg'),
    title: 'Modus Vivendi',
    author: 'by Jayson Castle',
    description:
        'A delicate balance of stillness and movement, presence and absence. It captures bodies in transformation, suspended in quiet resistance.',
    showing: 'Showing until 10 March 2026',
    venue: 'Tate Modern Exhibition',
    location: 'Bankside, London SE1 9TG',
    button: 'SHOWROOM',
    number: 'CONCEPT / MOTION ART',
    kicker: 'CONTEMPORARY / MOTION',
    conceptTitle: 'CONCEPT',
    conceptBody:
        'A study in rhythm, distortion, and controlled imbalance. Surfaces react to movement. Movement reshapes the frame. The piece shifts between clarity and noise, revealing patterns you only see when they break.',
    yearFrom: '2024',
    yearTo: '2025',
    code: 'NTRL',
    projectCode: '461.78.A.002',
    projectName: 'RIPPLE TRACE',
    version: 'AK1.0',
    details: [
        'VISUAL IDENTITY',
        'MOTION MAPPING',
        'ART DIRECTION',
        'CONCEPT DEVELOPMENT'
    ] as const
}

export const about = {
    index: 'NDX — A7',
    intro: 'I’am',
    name: 'Jayson Castle',
    subtitle: 'Developing work across digital and physical formats.',
    role: 'Independent Visual Artist',
    quote:
        '“Perfection is achieved not when there is nothing more to add, but when there is nothing left to take away.”',
    quoteAuthor: '— Antoine de Saint-Exupery',
    summary: 'I make work across image, form, motion, and text.',
    summaryNote:
        'Most projects start with a rule. Experience triggers the next step.',
    image: img('jjJ6XIKZWJ2rtvkJZuo8IqJCQHo.jpg'),
    profile: 'I’m a UK-based visual artist.',
    profileNote:
        'My practice is driven by experiments, systems, and iteration. Some work resolves quickly, others evolve over time.',
    skillsLabel: 'THINGS I DO',
    skills: [
        'VISUAL EXPERIMENTS',
        'FORM & FUNCTION',
        'SOUND Design',
        'Motion GRaphics',
        'WRITTEN FRAGMENTS',
        'Installation Studies',
        'STORYBOARDS',
        'Visual Scripts'
    ] as const,
    cta: 'VIEW THE WORK'
}
